import { getUseResource } from '../resource';
import { licenseResources } from '../builders/licenseResources';
import { ILicense } from '../../models/ILicense';
import { IOrganizationAllLicenseItem } from '../../models/IOrganizationAllLicenseItem';
import { IAssignLicensePayload } from '../../hooks/useUsersApi';
import { IAirgapRequestVersionResponse } from '../../types/AirgapRequestVersion';
import { IMachine } from '../../models/Machine';

interface ILicenseDetails extends ILicense {
  toolsLicenses: Array<{
    id: number;
    tool: string;
    key: string;
  }>;
  machine: IMachine;
}

export const apiLicense = {
  useGetLicenseDetails: getUseResource(licenseResources.getLicenseDetails, (body): ILicenseDetails => body),
  useGetUserLicenses: getUseResource(licenseResources.getUserLicenses, (body): ILicense[] => body),
  useAssignLicense: getUseResource(
    licenseResources.assignLicense,
    (body): void => body,
    (params, payload: IAssignLicensePayload) => payload
  ),
  useUnassignLicense: getUseResource(licenseResources.unassignLicense, (body): void => body),
  useGenerateAirgap: getUseResource(
    licenseResources.generateAirgap,
    (body): void => body,
    (params, file: File, basePackages: number[], addonPackages: number[]) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append(
        'data',
        new Blob(
          [
            JSON.stringify({
              basePackages,
              addonPackages,
            }),
          ],
          {
            type: 'application/json',
          }
        )
      );
      return formData;
    }
  ),
  useGenerateAirgapLegacy: getUseResource(
    licenseResources.generateAirgapLegacy,
    (body): void => body,
    (params, file: File, licenseId: number) => ({ file, licenseId }),
    (resource, apiExecutor) => {
      return async (payload): Promise<void> => {
        const resourceUrl = resource
          ? resource.template
              .replace(':user_id', resource.params.user_id)
              .replace(':organization_id', resource.params.organization_id)
              .replace(':license_id', payload.licenseId.toString())
          : null;

        if (!resourceUrl) {
          throw new Error('resource is missing');
        }

        const formData = new FormData();
        formData.append('file', payload.file);

        const response = await apiExecutor.post<void>(resourceUrl, formData);
        return response.data;
      };
    }
  ),
  useGetPixotopeVersion: getUseResource(
    licenseResources.getPixotopeVersion,
    (body): IAirgapRequestVersionResponse => body,
    (params, file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return formData;
    }
  ),
  useForceRelease: getUseResource(licenseResources.forceRelease, (body): void => body),
  useReleaseOnline: getUseResource(licenseResources.releaseOnline, (body): void => body),
  useReleaseAirgap: getUseResource(
    licenseResources.releaseAirgap,
    (body): void => body,
    (params, file: File, basePackages: number[], addonPackages: number[]) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append(
        'data',
        new Blob(
          [
            JSON.stringify({
              basePackages,
              addonPackages,
            }),
          ],
          {
            type: 'application/json',
          }
        )
      );
      return formData;
    }
  ),
  useReleaseAirgapLegacy: getUseResource(
    licenseResources.releaseAirgapLegacy,
    (body): void => body,
    (params, file: File, licenseId: number) => ({ file, licenseId }),
    (resource, apiExecutor) => {
      return async (payload): Promise<void> => {
        const resourceUrl = resource
          ? resource.template
              .replace(':user_id', resource.params.user_id)
              .replace(':organization_id', resource.params.organization_id)
              .replace(':license_id', payload.licenseId.toString())
          : null;

        if (!resourceUrl) {
          throw new Error('resource is missing');
        }

        const formData = new FormData();
        formData.append('file', payload.file);

        const response = await apiExecutor.post<void>(resourceUrl, formData);
        return response.data;
      };
    }
  ),
  useGetUnassignedLicenses: getUseResource(licenseResources.unassignedLicenses, (body): ILicense[] => body),
  useGetOrganizationLicenses: getUseResource(
    licenseResources.getOrganizationLicenses,
    (body): IOrganizationAllLicenseItem[] => body
  ),
};
